import { BigNumber } from "@yjsgames/bignumber";
import {
  LocalStorageSaveAdapter,
  migrateSave,
  versionRange,
  type SaveAdapter,
  type SaveMigrationRule,
} from "@yjsgames/save";
import {
  createDefaultModuleBrickSaveData,
  DAILY_RESET_KEY_DUNGEON_TICKETS,
  MODULE_BRICK_SAVE_VERSION,
  type ModuleBrickSaveData,
  type ModuleMasterySaveState,
} from "../models/ModuleBrickSaveData.js";
import type { GridPosition, ModuleGridState, PlacedModule } from "../models/ModuleGrid.js";
import type { ModuleInstance } from "../models/ModuleInstance.js";
import { ALL_MODULE_DEFINITIONS } from "../data/moduleDefinitions.js";
import { defaultPieceOf, FALLBACK_PIECE, type ModulePiece } from "../data/modulePieces.js";
import { footprintCells } from "../data/moduleFootprints.js";
import type { GuideQuestSaveState } from "@yjsgames/guide-quest";
import {
  DEFAULT_SPACESHIP_ID,
  moduleGridStateForSpaceship,
  sanitizeSpaceshipUnlockedCells,
  spaceshipById,
} from "../data/spaceshipData.js";

const SAVE_KEY = "module-brick-idle-save-v1";

/** 활성(enable=true) 모듈 id 집합 — 비활성화된 모듈(speed 등)의 잔존 인스턴스/배치/도감 정리에 사용. */
const ACTIVE_MODULE_IDS = new Set<string>(ALL_MODULE_DEFINITIONS.map((d) => d.id));

/** 정의 id → 기본 조각(pieces[0]) — 조각 모델 이전(v≤12) 인스턴스를 v13 마이그레이션 시 정의 조각으로 재산정. */
const DEFAULT_PIECE_BY_DEF = new Map<string, ModulePiece>(
  ALL_MODULE_DEFINITIONS.map((d) => [d.id, defaultPieceOf(d.pieces)]),
);

/** 로컬 스토리지 기반 세이브/로드. normalize로 버전 마이그레이션 + 필드 기본값 보정. */
export class ModuleBrickSaveSystem {
  constructor(
    private readonly adapter: SaveAdapter = new LocalStorageSaveAdapter(),
    private readonly key: string = SAVE_KEY,
  ) {}

  async save(data: ModuleBrickSaveData): Promise<void> {
    try {
      await this.adapter.save(this.key, JSON.stringify(data));
    } catch (error) {
      console.warn("[ModuleBrickSaveSystem] save failed:", (error as Error).message);
    }
  }

  async load(): Promise<ModuleBrickSaveData | null> {
    try {
      const raw = await this.adapter.load(this.key);
      if (raw === null) return null;
      return normalize(JSON.parse(raw));
    } catch (error) {
      console.warn("[ModuleBrickSaveSystem] load failed - starting new game:", (error as Error).message);
      return null;
    }
  }

  /**
   * 마이그레이션용 raw 파싱 — 정규화 전 원본 객체. 설정 분리(language/numberFormat → BrickSettings)를
   * 첫 부팅에서 1회 이전할 때 사용한다(load는 정규화 후라 제거된 설정 필드를 잃는다). 없으면 null.
   */
  async loadRaw(): Promise<Record<string, unknown> | null> {
    try {
      const raw = await this.adapter.load(this.key);
      if (raw === null) return null;
      const parsed: unknown = JSON.parse(raw);
      return typeof parsed === "object" && parsed !== null
        ? (parsed as Record<string, unknown>)
        : null;
    } catch {
      return null;
    }
  }

  async clear(): Promise<void> {
    try {
      await this.adapter.delete(this.key);
    } catch (error) {
      console.warn("[ModuleBrickSaveSystem] clear failed:", (error as Error).message);
    }
  }
}

/** number(구버전) | string(v6+) | 손상값 → BigNumber 직렬화 string. 손상 시 fallback. */
function coerceBig(v: unknown, fallback: number): string {
  if (typeof v === "number" && Number.isFinite(v)) return BigNumber.fromNumber(v).toString();
  if (typeof v === "string" && v.trim() !== "") return BigNumber.fromString(v).toString();
  return BigNumber.fromNumber(fallback).toString();
}

/**
 * 저장 버전 → 마이그레이션 룰 테이블 (선언형 — @yjsgames/save migrateSave 디스패치).
 * 범위 밖(v9·v<3·미래 버전)·비number version은 migrateSave가 null 반환 = 새 게임.
 * 새 버전 추가 = 범위 max 증가 또는 룰 행 1개.
 *  - v3~8  : 구 진행 보존 마이그레이션 + 그리드 배치 리셋(회전/shapeId 체계 변경)
 *  - v10~13: 조각 모델 정규화 (v13 reload는 조각·배치 보존, v10~12는 정의 기본 조각으로 재산정 + 배치 리셋)
 */
const MIGRATION_RULES: readonly SaveMigrationRule<ModuleBrickSaveData>[] = [
  { range: versionRange(3, 8), migrate: (raw, v) => migrateLegacyV3to8(raw, v) },
  { range: versionRange(10, MODULE_BRICK_SAVE_VERSION), migrate: (raw, v) => migrateV10Plus(raw, v) },
];

function normalize(parsed: unknown): ModuleBrickSaveData | null {
  return migrateSave(parsed, MIGRATION_RULES);
}

/**
 * v3~v8 → 현재 마이그레이션(진행 보존): language(v3 누락 시 detect) + footprintId="single" 백필
 * + gold/diamond number→BigNumber string 흡수 + numberFormat 기본 "auto"
 * + dungeonLastResetUtcMs(단일) → dailyResets["dungeon-tickets"] 맵 이관 (v7)
 * + permanentDamageBonus → rebirthUpgradeLevels.attack 이관(공격력 스탯 통합, v8)
 * + 모듈 그리드 배치 리셋(v9 회전 제거·shapeId 카탈로그 전환으로 구 배치 호환 불가 — 해금/인벤토리/골드는 보존).
 */
function migrateLegacyV3to8(raw: Record<string, unknown>, savedVersion: number): ModuleBrickSaveData {
  const p = raw as Partial<ModuleBrickSaveData>;
  const defaults = createDefaultModuleBrickSaveData();
  const migrated: ModuleBrickSaveData = {
    ...defaults,
    ...p,
    version: MODULE_BRICK_SAVE_VERSION,
    gold: coerceBig(raw["gold"], INITIAL_GOLD_FALLBACK),
    diamond: coerceBig(raw["diamond"], 0),
    dailyResets: normalizeDailyResets(raw["dailyResets"], raw["dungeonLastResetUtcMs"]),
    relicCounts: normalizeRelicCounts(raw["relicCounts"], raw["ownedRelicIds"]),
    rebirthUpgradeLevels: normalizeRebirthUpgradeLevels(
      raw["rebirthUpgradeLevels"],
      raw["permanentDamageBonus"],
    ),
    moduleLevels: normalizeModuleLevels(raw["moduleLevels"], raw["moduleInstances"]),
    synergyLevels: normalizeLevelMap(raw["synergyLevels"]),
    selectedSpaceshipId: normalizeSpaceshipId(raw["selectedSpaceshipId"]),
    ownedSpaceshipIds: normalizeOwnedSpaceshipIds(raw["ownedSpaceshipIds"], normalizeSpaceshipId(raw["selectedSpaceshipId"])),
    spaceshipModuleUnlocks: normalizeSpaceshipModuleUnlocks(raw["spaceshipModuleUnlocks"], raw["moduleGrid"], normalizeSpaceshipId(raw["selectedSpaceshipId"]), savedVersion),
    moduleMastery: {},
    guideQuest: normalizeGuideQuest(raw["guideQuest"]),
    // 구 그리드는 회전+구 shapeId 체계 — 배치만 비우고 해금(unlockedCells)·Start/Fire는 보존.
    moduleGrid: { ...(p.moduleGrid ?? defaults.moduleGrid), placedModules: [] },
  };
  return finalize(migrated, defaults, true, savedVersion);
}

/**
 * v10~현재 정규화 — 모듈 그리드 5×5 차원 동일. v13(조각 모델) 미만(10/11/12)에서 올라오면 인스턴스 조각
 * (shapeId/footprintId)을 정의 기본 조각으로 재산정 + 배치 리셋(finalize reassignPieces) — 구 전역 랜덤
 * footprint·shapeId 부재 흡수. 해금/인벤토리/골드/강화는 보존. v13 이상 reload는 조각·배치를 그대로 보존.
 * (v14 = 순수 field-add — mysteryDiamondGrantedToday/mysteryRelicGrantedToday 0 백필만, 배치 리셋 없음)
 */
function migrateV10Plus(raw: Record<string, unknown>, savedVersion: number): ModuleBrickSaveData {
  const p = raw as Partial<ModuleBrickSaveData>;
  const defaults = createDefaultModuleBrickSaveData();
  const normalized: ModuleBrickSaveData = {
    ...defaults,
    ...p,
    version: MODULE_BRICK_SAVE_VERSION,
    gold: coerceBig(raw["gold"], INITIAL_GOLD_FALLBACK),
    diamond: coerceBig(raw["diamond"], 0),
    dailyResets: normalizeDailyResets(raw["dailyResets"], raw["dungeonLastResetUtcMs"]),
    relicCounts: normalizeRelicCounts(raw["relicCounts"], raw["ownedRelicIds"]),
    rebirthUpgradeLevels: normalizeRebirthUpgradeLevels(
      raw["rebirthUpgradeLevels"],
      raw["permanentDamageBonus"],
    ),
    moduleLevels: normalizeModuleLevels(raw["moduleLevels"], raw["moduleInstances"]),
    synergyLevels: normalizeLevelMap(raw["synergyLevels"]),
    selectedSpaceshipId: normalizeSpaceshipId(raw["selectedSpaceshipId"]),
    ownedSpaceshipIds: normalizeOwnedSpaceshipIds(raw["ownedSpaceshipIds"], normalizeSpaceshipId(raw["selectedSpaceshipId"])),
    spaceshipModuleUnlocks: normalizeSpaceshipModuleUnlocks(raw["spaceshipModuleUnlocks"], raw["moduleGrid"], normalizeSpaceshipId(raw["selectedSpaceshipId"]), savedVersion),
    moduleMastery: savedVersion >= 25 ? normalizeModuleMastery(raw["moduleMastery"]) : {},
    guideQuest: normalizeGuideQuest(raw["guideQuest"]),
    adFree: raw["adFree"] === true,
  };
  // v13 미만(10/11/12)만 reassignPieces — v13+는 조각 모델 완성이므로 배치 보존.
  // v14는 순수 field-add(mysteryDiamondGrantedToday/Relic)라 v13→v14에서 배치 리셋 불필요.
  return finalize(normalized, defaults, savedVersion < 13, savedVersion);
}

/**
 * 유물 누적 개수 맵 정규화. 신규 relicCounts(relicId→count>0)만 남기고,
 * 구버전 ownedRelicIds 배열은 각 id를 count 1로 마이그레이션(relicCounts에 없을 때만).
 */
function normalizeRelicCounts(rawCounts: unknown, rawOwnedIds: unknown): Record<string, number> {
  const out: Record<string, number> = {};
  if (typeof rawCounts === "object" && rawCounts !== null && !Array.isArray(rawCounts)) {
    for (const [id, v] of Object.entries(rawCounts as Record<string, unknown>)) {
      if (typeof v === "number" && Number.isFinite(v) && v > 0) out[id] = Math.floor(v);
    }
  }
  if (Array.isArray(rawOwnedIds)) {
    for (const id of rawOwnedIds) {
      if (typeof id === "string" && out[id] === undefined) out[id] = 1;
    }
  }
  return out;
}

/**
 * 모듈 레벨 맵 정규화 (v17). 레벨은 moduleId별 플레이어 자산이라 인스턴스가 아니라 여기 저장된다.
 * 신규 moduleLevels(moduleId→level>1)를 우선 사용하고, 부재(v16 이하)면 구 moduleInstances[].level을
 * moduleId별 **max로 집계**해 이관한다(같은 모듈 인스턴스가 여러 개여도 최고 레벨을 플레이어 레벨로 승격).
 * level<=1은 기본값이라 저장하지 않는다(미존재=Lv1).
 */
function normalizeModuleLevels(rawLevels: unknown, rawInstances: unknown): Record<string, number> {
  const out: Record<string, number> = {};
  if (typeof rawLevels === "object" && rawLevels !== null && !Array.isArray(rawLevels)) {
    for (const [id, v] of Object.entries(rawLevels as Record<string, unknown>)) {
      if (typeof v === "number" && Number.isFinite(v) && v > 1) out[id] = Math.floor(v);
    }
    // 채워진 맵(v17+)이면 그대로 사용. 빈 맵(v17 모두 Lv1 또는 v16 base default {})이면 인스턴스 집계로 폴백.
    if (Object.keys(out).length > 0) return out;
  }
  // v16 이하 마이그레이션 — 인스턴스 level을 moduleId별 max로 집계.
  if (Array.isArray(rawInstances)) {
    for (const inst of rawInstances as Array<{ definitionId?: unknown; level?: unknown }>) {
      const id = inst?.definitionId;
      const lv = inst?.level;
      if (typeof id === "string" && typeof lv === "number" && Number.isFinite(lv) && lv > 1) {
        out[id] = Math.max(out[id] ?? 1, Math.floor(lv));
      }
    }
  }
  return out;
}

function normalizeLevelMap(rawLevels: unknown): Record<string, number> {
  const out: Record<string, number> = {};
  if (typeof rawLevels !== "object" || rawLevels === null || Array.isArray(rawLevels)) return out;
  for (const [id, value] of Object.entries(rawLevels as Record<string, unknown>)) {
    if (typeof value === "number" && Number.isFinite(value) && value > 1) out[id] = Math.floor(value);
  }
  return out;
}

/** 숙련도 XP 정규화 — 유한 number이고 >0이면 floor, 아니면 0. */
function coerceMasteryXp(v: unknown): number {
  return typeof v === "number" && Number.isFinite(v) && v > 0 ? Math.floor(v) : 0;
}

/**
 * 모듈 숙련도 맵 정규화 (v25). moduleId → {xp, selectedBranch?}.
 * v24 이하의 counters 기반 상태는 호출부에서 초기화한다. 여기서는 v25+ XP 상태만 정규화한다.
 */
function normalizeModuleMastery(raw: unknown): ModuleMasterySaveState {
  const out: ModuleMasterySaveState = {};
  if (typeof raw !== "object" || raw === null || Array.isArray(raw)) return out;
  for (const [moduleId, v] of Object.entries(raw as Record<string, unknown>)) {
    if (!ACTIVE_MODULE_IDS.has(moduleId)) continue;
    if (typeof v !== "object" || v === null) continue;
    const entry = v as { xp?: unknown; selectedBranch?: unknown };
    const branch = entry.selectedBranch;
    out[moduleId] = {
      xp: coerceMasteryXp(entry.xp),
      ...(branch === "A" || branch === "B" ? { selectedBranch: branch } : {}),
    };
  }
  return out;
}

function normalizeGuideQuest(raw: unknown): GuideQuestSaveState {
  if (typeof raw !== "object" || raw === null || Array.isArray(raw)) return { progress: {} };
  const progressRaw = (raw as { progress?: unknown }).progress;
  if (typeof progressRaw !== "object" || progressRaw === null || Array.isArray(progressRaw)) {
    return { progress: {} };
  }
  const progress: GuideQuestSaveState["progress"] = {};
  for (const [questId, entry] of Object.entries(progressRaw as Record<string, unknown>)) {
    if (typeof entry !== "object" || entry === null || Array.isArray(entry)) continue;
    const e = entry as Record<string, unknown>;
    const p = e["progress"];
    progress[questId] = {
      questId,
      progress: typeof p === "number" && Number.isFinite(p) ? Math.max(0, Math.floor(p)) : 0,
      completed: Boolean(e["completed"]),
      claimed: Boolean(e["claimed"]),
    };
  }
  return { progress };
}

function normalizeSpaceshipId(raw: unknown): string {
  return typeof raw === "string" && spaceshipById(raw) ? raw : DEFAULT_SPACESHIP_ID;
}

function normalizeOwnedSpaceshipIds(raw: unknown, selectedId: string): string[] {
  const out: string[] = [];
  const push = (id: string): void => {
    if (!spaceshipById(id) || out.includes(id)) return;
    out.push(id);
  };
  push(DEFAULT_SPACESHIP_ID);
  push(selectedId);
  if (Array.isArray(raw)) {
    for (const id of raw) if (typeof id === "string") push(id);
  }
  return out;
}

function normalizeCellList(raw: unknown): GridPosition[] {
  if (!Array.isArray(raw)) return [];
  const out: GridPosition[] = [];
  for (const cell of raw) {
    if (typeof cell !== "object" || cell === null || Array.isArray(cell)) continue;
    const c = cell as Record<string, unknown>;
    if (typeof c.x === "number" && Number.isInteger(c.x) && typeof c.y === "number" && Number.isInteger(c.y)) {
      out.push({ x: c.x, y: c.y });
    }
  }
  return out;
}

function normalizeSpaceshipModuleUnlocks(
  raw: unknown,
  rawModuleGrid: unknown,
  selectedId: string,
  savedVersion: number,
): Record<string, GridPosition[]> {
  const out: Record<string, GridPosition[]> = {};
  if (savedVersion < 28) {
    const selectedDef = spaceshipById(selectedId);
    if (selectedDef) out[selectedId] = sanitizeSpaceshipUnlockedCells(selectedDef, []);
    const defaultDef = spaceshipById(DEFAULT_SPACESHIP_ID);
    if (defaultDef && !out[DEFAULT_SPACESHIP_ID]) {
      out[DEFAULT_SPACESHIP_ID] = sanitizeSpaceshipUnlockedCells(defaultDef, []);
    }
    return out;
  }
  if (typeof raw === "object" && raw !== null && !Array.isArray(raw)) {
    for (const [id, cellsRaw] of Object.entries(raw as Record<string, unknown>)) {
      const def = spaceshipById(id);
      if (!def) continue;
      out[id] = sanitizeSpaceshipUnlockedCells(def, normalizeCellList(cellsRaw));
    }
  }
  if (!out[selectedId]) {
    const def = spaceshipById(selectedId);
    const rawGrid = rawModuleGrid as { unlockedCells?: unknown } | undefined;
    out[selectedId] = def
      ? sanitizeSpaceshipUnlockedCells(def, normalizeCellList(rawGrid?.unlockedCells))
      : [];
  }
  const defaultDef = spaceshipById(DEFAULT_SPACESHIP_ID);
  if (defaultDef && !out[DEFAULT_SPACESHIP_ID]) {
    out[DEFAULT_SPACESHIP_ID] = sanitizeSpaceshipUnlockedCells(defaultDef, []);
  }
  // v30: pioneer gridMap 변경(3x2 LLL/LLL → 2x3 ../LL/LL) — 상단 2칸 잠금 도입.
  // v29 이하 저장에서 pioneer의 {0,0},{1,0}이 해금으로 남아 있으면 강제 제거.
  if (savedVersion <= 29 && out["pioneer"]) {
    out["pioneer"] = out["pioneer"].filter(
      (c) => !(c.x === 0 && c.y === 0) && !(c.x === 1 && c.y === 0),
    );
  }
  return out;
}

/**
 * 환생 강화 레벨 맵 정규화 (v8). 신규 rebirthUpgradeLevels(statId→level>0)만 남기고,
 * 구버전 permanentDamageBonus(환생 1포인트당 기본 피해 +1)를 attack 레벨로 이관한다
 * (공격력 스탯 통합 흡수 — attack 키가 없을 때만, base=permanentDamagePerPoint=1로 동일 효과).
 */
function normalizeRebirthUpgradeLevels(
  rawLevels: unknown,
  rawPermanentDamageBonus: unknown,
): Record<string, number> {
  const out: Record<string, number> = {};
  if (typeof rawLevels === "object" && rawLevels !== null && !Array.isArray(rawLevels)) {
    for (const [id, v] of Object.entries(rawLevels as Record<string, unknown>)) {
      if (typeof v === "number" && Number.isFinite(v) && v > 0) out[id] = Math.floor(v);
    }
  }
  if (
    out["attack"] === undefined &&
    typeof rawPermanentDamageBonus === "number" &&
    Number.isFinite(rawPermanentDamageBonus) &&
    rawPermanentDamageBonus > 0
  ) {
    out["attack"] = Math.floor(rawPermanentDamageBonus);
  }
  return out;
}

/**
 * 일일 리셋 맵 정규화 — 유효한 number 값만 남긴다.
 * 구버전(v6 이하)에서 dailyResets 부재 시, dungeonLastResetUtcMs(>0)를 "dungeon-tickets" 키로 이관.
 * (0/누락은 비워둠 — DailyResetService.register가 현재 경계로 시드해 유령 리셋을 방지한다.)
 */
function normalizeDailyResets(rawResets: unknown, rawDungeonReset: unknown): Record<string, number> {
  const out: Record<string, number> = {};
  if (typeof rawResets === "object" && rawResets !== null) {
    for (const [key, value] of Object.entries(rawResets as Record<string, unknown>)) {
      if (typeof value === "number" && Number.isFinite(value)) out[key] = value;
    }
  }
  if (
    out[DAILY_RESET_KEY_DUNGEON_TICKETS] === undefined &&
    typeof rawDungeonReset === "number" &&
    rawDungeonReset > 0
  ) {
    out[DAILY_RESET_KEY_DUNGEON_TICKETS] = rawDungeonReset;
  }
  return out;
}

/** createDefault의 INITIAL_GOLD를 마이그레이션 fallback으로 재사용 (gold 손상 시). */
const INITIAL_GOLD_FALLBACK = BigNumber.fromString(
  createDefaultModuleBrickSaveData().gold,
).toNumber();

/**
 * 모든 마이그레이션/정규화 경로의 공통 마무리 단계. 세 책임을 수행한다:
 *  1. 비활성 모듈 정리 — ACTIVE_MODULE_IDS(enable=true 정의)에 없는 definitionId를
 *     moduleInstances·discoveredDefinitionIds·moduleGrid.placedModules에서 제거(speed 등 비활성화 흡수).
 *  2. 조각(piece) 정규화 — reassignPieces=true(조각 모델 이전 v≤12 마이그레이션)면 각 인스턴스의
 *     shapeId/footprintId를 정의 기본 조각(def.pieces[0])으로 재산정(구 전역 랜덤 footprint·shapeId 부재 흡수) + 배치 리셋.
 *     v13+ reload(v14 포함)는 reassignPieces=false라 인스턴스 조각/배치를 그대로 보존(누락만 백필).
 *  3. 누락/구 레이아웃 보정 — moduleGrid를 선택 우주선 레이아웃 기준으로 정규화(normalizeModuleGrid).
 */
function finalize(
  data: ModuleBrickSaveData,
  defaults: ModuleBrickSaveData,
  reassignPieces: boolean,
  savedVersion: number,
): ModuleBrickSaveData {
  const placedModules = reassignPieces
    ? []
    : (data.moduleGrid?.placedModules
        ?.filter((m) => ACTIVE_MODULE_IDS.has((m as PlacedModule).definitionId))
        .map((m) => ({
            ...m,
            shapeId: (m as PlacedModule).shapeId ?? "straight_h",
            footprintId: (m as PlacedModule).footprintId ?? "single",
          })) ??
      defaults.moduleGrid.placedModules.map((m) => ({ ...m })));
  return {
    ...data,
    // 비활성(enable=false)된 모듈의 잔존 인스턴스/도감을 제거 + 조각 정규화/백필.
    moduleInstances: (data.moduleInstances ?? [])
      .filter((inst) => ACTIVE_MODULE_IDS.has(inst.definitionId))
      .map((inst) => {
        if (reassignPieces) {
          // 조각 모델 이전 세이브 — 정의 기본 조각으로 재산정(구 footprint 무시, shapeId 부여).
          const piece = DEFAULT_PIECE_BY_DEF.get(inst.definitionId) ?? FALLBACK_PIECE;
          return { ...inst, shapeId: piece.shapeId, footprintId: piece.footprintId };
        }
        const i = inst as ModuleInstance;
        return { ...inst, shapeId: i.shapeId ?? "straight_h", footprintId: i.footprintId ?? "single" };
      }),
    discoveredDefinitionIds: (data.discoveredDefinitionIds ?? []).filter((id) =>
      ACTIVE_MODULE_IDS.has(id),
    ),
    moduleGrid: normalizeModuleGrid(
      data.moduleGrid,
      defaults.moduleGrid,
      placedModules,
      savedVersion,
      data.selectedSpaceshipId,
      data.spaceshipModuleUnlocks,
    ),
  };
}

/**
 * 저장 해금 칸 ∪ 초기 해금 칸 (멱등). 기존 세이브가 신규 초기 칸(중앙행)을 이미 구매했던 경우
 * purchasedCount(보유 − INITIAL)가 그만큼 감소해 해금 비용이 일시 하락 — 플레이어 우호 일회성으로 수용.
 */
function unionCells(saved: GridPosition[], initial: GridPosition[]): GridPosition[] {
  const merged = saved.map((c) => ({ ...c }));
  for (const c of initial) {
    if (!merged.some((m) => m.x === c.x && m.y === c.y)) merged.push({ ...c });
  }
  return merged;
}

const LEGACY_V25_FREE_EXTRA_CELLS: readonly GridPosition[] = [
  { x: 0, y: 3 },
  { x: 1, y: 3 },
  { x: 2, y: 3 },
];

function cellEquals(a: GridPosition, b: GridPosition): boolean {
  return a.x === b.x && a.y === b.y;
}

function placementCoversCell(module: PlacedModule, cell: GridPosition): boolean {
  return footprintCells(module.footprintId).some((offset) => module.x + offset.dx === cell.x && module.y + offset.dy === cell.y);
}

function placementFitsGrid(module: PlacedModule, grid: ModuleGridState): boolean {
  return footprintCells(module.footprintId).every((offset) => {
    const cell = { x: module.x + offset.dx, y: module.y + offset.dy };
    return (
      cell.x >= 0 &&
      cell.y >= 0 &&
      cell.x < grid.width &&
      cell.y < grid.height &&
      !(grid.blockedCells ?? []).some((blocked) => cellEquals(blocked, cell)) &&
      grid.unlockedCells.some((unlocked) => cellEquals(unlocked, cell))
    );
  });
}

function normalizeUnlockedCells(
  grid: ModuleGridState | undefined,
  defaults: ModuleGridState,
  placedModules: readonly PlacedModule[],
  savedVersion: number,
): GridPosition[] {
  let cells = unionCells(grid?.unlockedCells ?? [], defaults.unlockedCells);
  if (savedVersion <= 25) {
    cells = cells.filter((cell) => {
      const wasLegacyFreeExtra = LEGACY_V25_FREE_EXTRA_CELLS.some((legacy) => cellEquals(legacy, cell));
      if (!wasLegacyFreeExtra) return true;
      return placedModules.some((module) => placementCoversCell(module, cell));
    });
  }
  return cells;
}

/**
 * 저장된 그리드 상태를 선택 우주선 레이아웃 기준으로 정규화한다.
 * 저장 크기가 현재 우주선과 다르면 width/height/lanes/blocked/baseUnlocked/unlocked를 레이아웃 기본값으로 맞추고
 * stale 배치는 제거한다. 크기가 맞는 저장은 레이아웃 밖 배치만 제거하고 endpoint/형태 필드는 최신값으로 덮어쓴다.
 * resetPlacements=true(조각 모델 이전 마이그레이션)면 배치를 비운다 — 조각(shape+footprint) 변경으로 점유가 달라질 수 있어
 * 기존 배치가 무효. 인벤토리는 보존되므로 플레이어가 재배치한다(v9 회전 제거 선례 동형).
 */
function normalizeModuleGrid(
  grid: ModuleGridState | undefined,
  defaults: ModuleGridState,
  placedModules: readonly PlacedModule[],
  savedVersion: number,
  selectedSpaceshipId: string,
  spaceshipModuleUnlocks: Record<string, GridPosition[]>,
): ModuleGridState {
  const def = spaceshipById(selectedSpaceshipId);
  const layoutDefaults = def
    ? moduleGridStateForSpaceship(def, spaceshipModuleUnlocks[selectedSpaceshipId])
    : defaults;
  if (savedVersion < 27) {
    return layoutDefaults;
  }
  const layoutMatches =
    grid?.width === layoutDefaults.width &&
    grid?.height === layoutDefaults.height;
  const safePlacedModules = layoutMatches
    ? placedModules.filter((module) => placementFitsGrid(module, layoutDefaults))
    : [];
  return {
    ...layoutDefaults,
    ...(layoutMatches ? grid : {}),
    width: layoutDefaults.width,
    height: layoutDefaults.height,
    start: { ...layoutDefaults.start },
    fire: { ...layoutDefaults.fire },
    lanes: layoutDefaults.lanes?.map((lane) => ({ start: { ...lane.start }, fire: { ...lane.fire } })),
    blockedCells: layoutDefaults.blockedCells?.map((cell) => ({ ...cell })),
    baseUnlockedCount: layoutDefaults.baseUnlockedCount,
    // 선택 우주선 레이아웃 해금 기준으로 재정규화. v25 이하 구 무료 하단 3칸은 배치가 없으면 다시 잠금으로 접는다.
    unlockedCells: normalizeUnlockedCells(
      layoutDefaults,
      layoutDefaults,
      safePlacedModules,
      savedVersion,
    ),
    placedModules: safePlacedModules.map((m) => ({ ...m })),
  };
}
